import { useState, useRef } from 'react';
import { MessageSquare, X, Send, Bug, Sparkles, Loader, CheckCircle, Paperclip, Image as ImageIcon, Film, Trash2 } from 'lucide-react';
import { useTheme } from './ThemeContext';
import { supabase } from './supabase';

const ALLOWED_TYPES = [
  'image/jpeg', 'image/png', 'image/gif', 'image/webp', 'image/heic',
  'video/mp4', 'video/quicktime', 'video/webm', 'video/x-msvideo',
];
const MAX_FILE_MB = 50;
const MAX_ATTACHMENTS = 4;

export default function FeedbackModal({ open, onClose, companyId, user, companyName }) {
  const { colors } = useTheme();
  const [type, setType] = useState('general');
  const [message, setMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState(null);
  const [attachments, setAttachments] = useState([]); // [{name, size, type, url}]
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  if (!open) return null;

  const handleFilePick = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    if (attachments.length + files.length > MAX_ATTACHMENTS) {
      setError(`You can attach up to ${MAX_ATTACHMENTS} files.`);
      return;
    }
    setError(null);
    setUploading(true);

    try {
      const newAttachments = [];
      for (const file of files) {
        if (!ALLOWED_TYPES.includes(file.type)) {
          setError(`Unsupported file type: ${file.name}. Use image or video only.`);
          continue;
        }
        if (file.size > MAX_FILE_MB * 1024 * 1024) {
          setError(`${file.name} is larger than ${MAX_FILE_MB}MB.`);
          continue;
        }

        const safeName = file.name.replace(/[^a-zA-Z0-9._-]/g, '_');
        const path = `${user?.id || 'anon'}/${Date.now()}-${safeName}`;
        const { error: upErr } = await supabase.storage
          .from('feedback-attachments')
          .upload(path, file, { contentType: file.type, cacheControl: '3600' });

        if (upErr) {
          setError(`Upload failed for ${file.name}: ${upErr.message}`);
          continue;
        }

        const { data: pub } = supabase.storage.from('feedback-attachments').getPublicUrl(path);
        newAttachments.push({
          name: file.name,
          size: file.size,
          type: file.type,
          url: pub.publicUrl,
          path,
        });
      }

      if (newAttachments.length) {
        setAttachments(prev => [...prev, ...newAttachments]);
      }
    } catch (err) {
      setError(err.message || 'Upload failed');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const removeAttachment = async (att) => {
    // Best-effort delete from storage; UI removes regardless
    try { await supabase.storage.from('feedback-attachments').remove([att.path]); } catch (_) {}
    setAttachments(prev => prev.filter(a => a.path !== att.path));
  };

  const handleSubmit = async () => {
    if (!message.trim() || message.trim().length < 3) {
      setError('Please write a bit more before sending');
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const resp = await fetch('/api/feedback', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          companyId,
          userId: user?.id,
          submittedByName: user?.user_metadata?.full_name || companyName || user?.email,
          submittedByEmail: user?.email,
          type,
          message: message.trim(),
          url: typeof window !== 'undefined' ? window.location.href : null,
          userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : null,
          attachments,
        }),
      });
      const data = await resp.json();
      if (!resp.ok || !data.success) {
        setError(data.error || 'Failed to send feedback');
        setSubmitting(false);
        return;
      }
      setSubmitted(true);
      setSubmitting(false);
      setTimeout(() => { reset(); onClose(); }, 1800);
    } catch (err) {
      setError(err.message || 'Failed to send feedback');
      setSubmitting(false);
    }
  };

  const reset = () => {
    setMessage('');
    setType('general');
    setSubmitted(false);
    setError(null);
    setAttachments([]);
  };

  const typeButton = (val, label, Icon) => (
    <button
      onClick={() => setType(val)}
      style={{
        flex: 1,
        padding: '0.6rem',
        background: type === val ? colors.accent : colors.bgInput,
        color: type === val ? '#000' : colors.textMuted,
        border: `1px solid ${type === val ? colors.accent : colors.border}`,
        borderRadius: '8px',
        fontWeight: 600,
        fontSize: '0.85rem',
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '0.35rem',
      }}
    >
      <Icon size={14} /> {label}
    </button>
  );

  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', zIndex: 3000,
      display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem',
    }}>
      <div style={{
        background: colors.bgCard, borderRadius: '12px',
        maxWidth: '480px', width: '100%', border: `1px solid ${colors.border}`,
        overflow: 'hidden',
      }}>
        <div style={{
          padding: '1rem 1.25rem', borderBottom: `1px solid ${colors.border}`,
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', color: colors.text, fontWeight: 600 }}>
            <MessageSquare size={18} />
            Send Feedback
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: colors.textMuted, cursor: 'pointer' }}>
            <X size={20} />
          </button>
        </div>

        <div style={{ padding: '1.25rem' }}>
          {submitted ? (
            <div style={{ textAlign: 'center', padding: '1rem 0' }}>
              <CheckCircle size={48} color="#4ecca3" style={{ marginBottom: '0.75rem' }} />
              <div style={{ color: colors.text, fontSize: '1.05rem', fontWeight: 600, marginBottom: '0.25rem' }}>
                Thanks — sent.
              </div>
              <div style={{ color: colors.textMuted, fontSize: '0.85rem' }}>
                The founder gets it directly.
              </div>
            </div>
          ) : (
            <>
              <div style={{ color: colors.textMuted, fontSize: '0.85rem', marginBottom: '0.9rem' }}>
                Goes straight to the founder. Bugs, features, questions — all welcome.
              </div>

              <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.9rem' }}>
                {typeButton('bug', 'Bug', Bug)}
                {typeButton('feature', 'Feature', Sparkles)}
                {typeButton('general', 'General', MessageSquare)}
              </div>

              <textarea
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder={
                  type === 'bug' ? 'What broke? What did you expect?' :
                  type === 'feature' ? 'What would make this easier?' :
                  'What\'s on your mind?'
                }
                rows={6}
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  background: colors.bgInput,
                  border: `1px solid ${colors.border}`,
                  borderRadius: '8px',
                  color: colors.text,
                  fontSize: '0.95rem',
                  resize: 'vertical',
                  fontFamily: 'inherit',
                  boxSizing: 'border-box',
                }}
              />

              {/* Attachments */}
              <div style={{ marginTop: '0.75rem' }}>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept={ALLOWED_TYPES.join(',')}
                  multiple
                  style={{ display: 'none' }}
                  onChange={handleFilePick}
                />
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploading || attachments.length >= MAX_ATTACHMENTS}
                  style={{
                    display: 'inline-flex', alignItems: 'center', gap: '0.4rem',
                    padding: '0.5rem 0.75rem',
                    background: 'transparent',
                    border: `1px dashed ${colors.border}`,
                    borderRadius: '6px',
                    color: colors.textMuted,
                    fontSize: '0.85rem',
                    cursor: uploading || attachments.length >= MAX_ATTACHMENTS ? 'not-allowed' : 'pointer',
                  }}
                >
                  {uploading ? <Loader size={14} style={{ animation: 'spin 1s linear infinite' }} /> : <Paperclip size={14} />}
                  {uploading ? 'Uploading...' : `Attach screenshot or video (${attachments.length}/${MAX_ATTACHMENTS})`}
                </button>

                {attachments.length > 0 && (
                  <div style={{ marginTop: '0.5rem', display: 'flex', flexDirection: 'column', gap: '0.35rem' }}>
                    {attachments.map(att => (
                      <div key={att.path} style={{
                        display: 'flex', alignItems: 'center', gap: '0.5rem',
                        padding: '0.4rem 0.6rem',
                        background: colors.bgInput,
                        border: `1px solid ${colors.border}`,
                        borderRadius: '6px',
                        fontSize: '0.8rem',
                      }}>
                        {att.type.startsWith('video/') ? <Film size={14} color={colors.textMuted} /> : <ImageIcon size={14} color={colors.textMuted} />}
                        <span style={{ color: colors.text, flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {att.name}
                        </span>
                        <span style={{ color: colors.textMuted, fontSize: '0.75rem' }}>
                          {(att.size / (1024 * 1024)).toFixed(1)}MB
                        </span>
                        <button
                          onClick={() => removeAttachment(att)}
                          type="button"
                          style={{ background: 'none', border: 'none', color: colors.textMuted, cursor: 'pointer', padding: '0.15rem' }}
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {error && (
                <div style={{
                  marginTop: '0.6rem', padding: '0.5rem 0.75rem',
                  background: '#e74c3c20', borderRadius: '6px',
                  color: '#e74c3c', fontSize: '0.85rem',
                }}>
                  {error}
                </div>
              )}

              <button
                onClick={handleSubmit}
                disabled={submitting || message.trim().length < 3}
                style={{
                  width: '100%',
                  marginTop: '0.9rem',
                  padding: '0.85rem',
                  background: submitting || message.trim().length < 3 ? colors.bgInput : colors.accent,
                  border: 'none',
                  borderRadius: '8px',
                  color: submitting || message.trim().length < 3 ? colors.textMuted : '#000',
                  fontWeight: 700,
                  fontSize: '1rem',
                  cursor: submitting || message.trim().length < 3 ? 'not-allowed' : 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '0.5rem',
                }}
              >
                {submitting ? (
                  <>
                    <Loader size={16} style={{ animation: 'spin 1s linear infinite' }} />
                    Sending...
                  </>
                ) : (
                  <>
                    <Send size={16} />
                    Send Feedback
                  </>
                )}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
